import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useRef, useState } from "react";
import type { FileField } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { encryptSecret } from "../../encryptSecret";
import { Box } from "../Box";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import type { App } from "./Form";

const UploadButton = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: var(--surface-primary);
  color: var(--text-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  border: 0;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  height: 32px;
  cursor: pointer;
  transition: box-shadow 100ms ease-out;
  &:hover {
    background: var(--alpha4);
  }
`;

const SavedRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

type Props = {
  item: FileField;
  app: App;
  disabled?: boolean;
};

export function FileFieldItem({ item, app, disabled }: Props) {
  const z = useZero();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const { target } = item;

  // `<input type="file">` renders empty after save, so the only way to show
  // "already uploaded" on re-open is to check the saved value. Secrets are
  // non-replicated (services/db/nonReplicatedColumns.ts) so we can't tell.
  const savedValue =
    target.kind === "configuration"
      ? app.configuration?.[target.field]
      : undefined;
  const hasSaved = typeof savedValue === "string" && savedValue.length > 0;

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = async () => {
      const text = reader.result as string;
      setFileName(file.name);

      const base = {
        connectedAppId: app.id,
        name: app.name,
        identifier: app.identifier,
        urlPattern: app.urlPattern,
        configuration: app.configuration,
      };

      if (target.kind === "secrets") {
        z.mutate(
          sharedMutators.updateConnectedApp({
            ...base,
            secrets: { [target.field]: await encryptSecret(text) },
          })
        );
      } else if (target.kind === "configuration") {
        z.mutate(
          sharedMutators.updateConnectedApp({
            ...base,
            configuration: { ...app.configuration, [target.field]: text },
          })
        );
      }
    };
    reader.readAsText(file);
  };

  const buttonLabel = fileName
    ? fileName
    : hasSaved
      ? "Replace file…"
      : "Choose file…";

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={item.accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
        }}
      />
      <SavedRow>
        <UploadButton
          onPress={() => inputRef.current?.click()}
          disabled={disabled}
        >
          <Icon name="upload" size={12} />
          <Text variant="text-small">{buttonLabel}</Text>
        </UploadButton>
        {hasSaved && !fileName && (
          <Box horizontal={4}>
            <Text variant="text-small" color="var(--text-secondary)">
              On file
            </Text>
          </Box>
        )}
      </SavedRow>
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {item.helperText}
          </Text>
        </Box>
      )}
    </div>
  );
}
